import React, { useState, useEffect } from 'react';
import api, { getApiErrorMessage } from '../../../services/api';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { EmptyState } from '../../../components/layout/SharedComponents';
import { ChevronLeft, Package, ShoppingCart, X } from 'lucide-react';

export default function AddPunchoutModal({ open, onClose, onAddItems, requestNumber }) {
  const [suppliers, setSuppliers] = useState([]);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [punchoutUrl, setPunchoutUrl] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) { setSelectedSupplier(null); setPunchoutUrl(null); return; }
    api.get('/api/data/suppliers', { params: { page_size: 100, punchout: 'true' } }).then(res => {
      setSuppliers(res.data.data || []);
    }).catch(() => {});
  }, [open]);

  // Listen for cart items from punchout iframe
  useEffect(() => {
    if (!open) return;
    const handler = async (e) => {
      if (e.data?.type === 'punchout-cart-req' && e.data.request === requestNumber) {
        const items = e.data.items || [];
        const supplierCode = e.data.supplier_code || '';
        if (onAddItems) {
          await onAddItems(items.map(it => ({
            item_code: it.item_code || it.supplier_part_number || '',
            item_description: it.item_description || '',
            quantity: it.quantity || 1,
            unit: it.unit || 'EACH',
            unit_price: it.unit_price || 0,
            pack_size: 1,
            discount: 0,
            supplier_code: supplierCode,
            suppliers_part_number: it.supplier_part_number || '',
          })));
        }
        toast.success(`${items.length} punchout item(s) added — continue shopping or close`);
      } else if (e.data?.type === 'punchout-error' && e.data.request === requestNumber) {
        toast.error(e.data.error || 'Punchout failed');
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [open, requestNumber, onAddItems]);

  const launchCatalog = async (supplier) => {
    setSelectedSupplier(supplier);
    setLoading(true);
    try {
      const r = await api.post(`/api/punchout/setup-requisition/${requestNumber}`, {
        supplier_code: supplier.supplier_code
      });
      setPunchoutUrl(r.data.start_url);
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Failed to connect to supplier catalog'));
      setSelectedSupplier(null);
    }
    setLoading(false);
  };

  return (
    <Dialog open={open} onOpenChange={() => { if (!loading) onClose(); }}>
      <DialogContent className={`flex flex-col ${punchoutUrl ? 'max-w-[95vw] max-h-[90vh]' : 'max-w-3xl max-h-[80vh]'}`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShoppingCart className="h-5 w-5 text-purple-600" />
            {punchoutUrl ? `${selectedSupplier?.supplier_name} — PunchOut Catalog` : 'Add from PunchOut Catalog'}
          </DialogTitle>
        </DialogHeader>

        {!selectedSupplier ? (
          <>
            <p className="text-sm text-muted-foreground mb-4">Select a punchout vendor to browse their catalog:</p>
            {suppliers.length === 0 ? (
              <EmptyState title="No punchout vendors found" description="Enable punchout on a supplier record under Punchout Details." />
            ) : (
              <div className="grid grid-cols-2 gap-3 overflow-y-auto">
                {suppliers.map(s => (
                  <button key={s.supplier_code}
                    className="flex items-start gap-3 p-4 border rounded-xl hover:border-purple-400 hover:bg-purple-50 text-left transition-all"
                    onClick={() => launchCatalog(s)}>
                    <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0">
                      <ShoppingCart className="h-4 w-4 text-purple-600" />
                    </div>
                    <div className="min-w-0">
                      <p className="font-semibold text-sm">{s.supplier_name}</p>
                      <p className="text-xs text-muted-foreground">{s.supplier_code}</p>
                      <Badge variant="outline" className="text-[9px] mt-1 border-purple-200 text-purple-600">
                        {s.punchout_details?.protocol || 'cXML'}
                      </Badge>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </>
        ) : punchoutUrl ? (
          <div className="flex-1 flex flex-col min-h-0">
            <div className="flex items-center justify-between mb-2">
              <button onClick={() => { setPunchoutUrl(null); setSelectedSupplier(null); }}
                className="text-sm text-primary hover:underline flex items-center gap-1">
                <ChevronLeft className="h-4 w-4" /> Back to Vendors
              </button>
              <Button size="sm" variant="outline" onClick={onClose}>
                <X className="h-3 w-3 mr-1" /> Done Shopping
              </Button>
            </div>
            <iframe
              src={punchoutUrl}
              className="w-full flex-1 border rounded-lg"
              style={{ minHeight: '550px' }}
              title="Supplier Punchout Catalog"
            />
          </div>
        ) : (
          <div className="py-12 text-center">
            <ShoppingCart className="h-10 w-10 text-purple-400 mx-auto mb-3 animate-pulse" />
            <p className="text-sm text-purple-600 font-semibold">Connecting to {selectedSupplier.supplier_name} catalog...</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
